import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject;

import React, { useState, useEffect, useContext } from 'react';
import DateRangePicker from 'react-daterange-picker';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import styled from 'styled-components';
import { setLightness } from 'polished';
import { Space, Col, Row } from 'antd';
import { identity } from 'lodash-es';
import 'react-daterange-picker/dist/css/react-calendar.css';
import Button from '../Button';
import { TmsUIContext } from '../../TmsUIProvider';
import translations from './translations';
var moment = extendMoment(Moment);
var STANDARD_DATE_FORMAT = 'YYYY-MM-DD';
var StyledDateRangePicker = styled(DateRangePicker)(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  color: ", ";\n  font-family: ", ";\n\n  .DateRangePicker__Date--is-selected {\n    .DateRangePicker__CalendarSelection {\n      background-color: ", ";\n      border: none;\n\n      &.DateRangePicker__CalendarSelection--single {\n        background-color: ", ";\n        border-radius: 0.2rem;\n        left: 0;\n        right: 0;\n      }\n\n      &.DateRangePicker__CalendarSelection--start, &.DateRangePicker__CalendarSelection--end {\n        background-color: ", ";\n        left: 0;\n        right: 0;\n      }\n\n      &.DateRangePicker__CalendarSelection--start {\n        border-bottom-left-radius: 0.2rem;\n        border-top-left-radius: 0.2rem;\n      }\n\n      &.DateRangePicker__CalendarSelection--end {\n        border-top-right-radius: 0.2rem;\n        border-bottom-right-radius: 0.2rem;\n      }\n    }\n    .DateRangePicker__DateLabel {\n      color: ", ";\n    }\n  }\n\n  .DateRangePicker__Date--is-highlighted {\n    .DateRangePicker__CalendarHighlight {\n      background-color: transparent;\n      border-color: ", ";\n    }\n  }\n\n  .DateRangePicker__PaginationArrow {\n    .DateRangePicker__PaginationArrowIcon--previous {\n      border-right-color: ", ";\n    }\n    .DateRangePicker__PaginationArrowIcon--next {\n      border-left-color: ", ";\n    }\n\n    &:hover {\n      background-color: transparent;\n\n      .DateRangePicker__PaginationArrowIcon--previous {\n        border-right-color: ", ";\n      }\n      .DateRangePicker__PaginationArrowIcon--next {\n        border-left-color: ", ";\n      }\n    }\n  }\n"])), function (props) {
  return props.theme.colors.dark;
}, function (props) {
  return props.theme.fontFamily;
}, function (props) {
  return setLightness(0.92, props.theme.colors.primary);
}, function (props) {
  return props.theme.colors.primary;
}, function (props) {
  return props.theme.colors.primary;
}, function (props) {
  return props.theme.colors.light;
}, function (props) {
  return props.theme.colors.primary;
}, function (props) {
  return props.theme.colors.dark;
}, function (props) {
  return props.theme.colors.dark;
}, function (props) {
  return props.theme.colors.primary;
}, function (props) {
  return props.theme.colors.primary;
});

function nDaysAgo(n) {
  return moment().subtract(n, 'd').startOf('day').toDate();
}

var rangeShortcutDictionary = {
  past_seven_days: {
    start: moment(nDaysAgo(6)).format(STANDARD_DATE_FORMAT),
    end: moment().endOf('day').format(STANDARD_DATE_FORMAT)
  },
  this_month: {
    start: moment().startOf('month').format(STANDARD_DATE_FORMAT),
    end: (moment().endOf('month').isAfter(moment()) ? moment().endOf('day') : moment().endOf('month')).format(STANDARD_DATE_FORMAT)
  },
  previous_month: {
    start: moment().subtract(1, 'month').startOf('month').format(STANDARD_DATE_FORMAT),
    end: moment().subtract(1, 'month').endOf('month').format(STANDARD_DATE_FORMAT)
  },
  past_three_months: {
    start: moment().subtract(3, 'month').startOf('month').format(STANDARD_DATE_FORMAT),
    end: moment().subtract(1, 'month').endOf('month').format(STANDARD_DATE_FORMAT)
  },
  this_year: {
    start: moment().startOf('year').format(STANDARD_DATE_FORMAT),
    end: (moment().endOf('year').isAfter(moment()) ? moment().endOf('day') : moment().endOf('year')).format(STANDARD_DATE_FORMAT)
  }
}; // TODO: add proper interface and tests
// tests are failed because jest can't transpile some packages

function KargoDateRangePicker(_ref) {
  var dataTestId = _ref['data-testid'],
      _ref$dateRangePickerC = _ref.dateRangePickerColProps,
      dateRangePickerColProps = _ref$dateRangePickerC === void 0 ? {} : _ref$dateRangePickerC,
      maximumDate = _ref.maximumDate,
      minimumDate = _ref.minimumDate,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? identity : _ref$onChange,
      _ref$predefinedDateCo = _ref.predefinedDateColProps,
      predefinedDateColProps = _ref$predefinedDateCo === void 0 ? {} : _ref$predefinedDateCo,
      singleDateRange = _ref.singleDateRange,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? null : _ref$value,
      _ref$wrapperRowProps = _ref.wrapperRowProps,
      wrapperRowProps = _ref$wrapperRowProps === void 0 ? {} : _ref$wrapperRowProps;

  /**
   * react-daterange-picker have an issue that whenever we changed value props back to null,
   * it'll somehow causing browser hang, so this 'render' and 'localValue' state is a workaround
   * to force react-daterange-picker component re-render
   */
  var _useContext = useContext(TmsUIContext),
      setDefaultTranslations = _useContext.setDefaultTranslations,
      translate = _useContext.translate;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      render = _useState2[0],
      setRender = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      localValue = _useState4[0],
      setLocalValue = _useState4[1];

  useEffect(function () {
    setDefaultTranslations(translations);
  }, [setDefaultTranslations]);

  var PredefinedDate = function PredefinedDate() {
    return /*#__PURE__*/React.createElement(Space, {
      direction: "vertical"
    }, Object.keys(rangeShortcutDictionary).map(function (shortcutKey) {
      return /*#__PURE__*/React.createElement(Button, {
        key: shortcutKey,
        block: true,
        colorScheme: value && moment(value.start).startOf('day').format(STANDARD_DATE_FORMAT) === rangeShortcutDictionary[shortcutKey].start && moment(value.end).endOf('day').format(STANDARD_DATE_FORMAT) === rangeShortcutDictionary[shortcutKey].end ? 'primary' : 'lightGreyBlue',
        onClick: function onClick() {
          return onChange({
            start: moment(rangeShortcutDictionary[shortcutKey].start).startOf('day').toDate(),
            end: moment(rangeShortcutDictionary[shortcutKey].end).endOf('day').toDate()
          });
        },
        style: {
          marginRight: 0
        }
      }, translate("common:".concat(shortcutKey)));
    }));
  };

  useEffect(function () {
    if (!value) {
      setLocalValue(null);
      setRender(render + 1);
      return;
    }

    setLocalValue(moment.range(value.start ? moment(value.start).startOf('day') : null, value.end ? moment(value.end).endOf('day') : null)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, setRender, setLocalValue]);
  return /*#__PURE__*/React.createElement(Row, _extends({
    "data-testid": dataTestId
  }, wrapperRowProps), /*#__PURE__*/React.createElement(Col, _extends({
    sm: 8,
    xs: 24
  }, predefinedDateColProps), /*#__PURE__*/React.createElement(PredefinedDate, null)), /*#__PURE__*/React.createElement(Col, _extends({
    sm: 16,
    xs: 0
  }, dateRangePickerColProps), /*#__PURE__*/React.createElement(StyledDateRangePicker, {
    key: render,
    maximumDate: maximumDate,
    minimumDate: minimumDate,
    onSelect: function onSelect(newValue) {
      return onChange({
        start: moment(newValue.start).startOf('day').toDate(),
        end: moment(newValue.end).endOf('day').toDate()
      });
    },
    singleDateRange: singleDateRange,
    value: localValue
  })));
}

export default KargoDateRangePicker;